<template>
  <jet-action-section>
    <template #title>{{ t("logout_other_sessions") }}</template>

    <template #description>
      {{ t("logout_other_sessions_description") }}</template
    >

    <template #content>
      <div class="max-w-xl text-sm text-gray-600">
        {{ t("explanation") }}
      </div>

      <div class="mt-5 space-y-6" v-if="sessions.length > 0">
        <div
          class="flex items-center"
          v-for="(session, i) in sessions"
          :key="i"
        >
          <div>
            <DeviceMobileIcon
              v-if="session.agent.is_desktop"
              class="w-8 h-8 text-gray-500"
            />
            <DesktopComputerIcon v-else class="w-8 h-8 text-gray-500" />
          </div>

          <div class="ml-3">
            <div class="text-sm text-gray-600">
              {{ session.agent.platform }} - {{ session.agent.browser }}
            </div>

            <div>
              <div class="text-xs text-gray-500">
                {{ session.ip_address }},

                <span
                  class="text-green-500 font-semibold"
                  v-if="session.is_current_device"
                  >{{ t("this_device") }}</span
                >
                <span v-else
                  >{{ t("last_active") }} {{ session.last_active }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center mt-5 justify-end">
        <ui-button type="button" @click="confirmLogout">
          {{ t("logout") }}
        </ui-button>
      </div>

      <ui-modal :show="confirmingLogout" @close="closeModal">
        <div class="sm:flex sm:items-start">
          <div
            class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
          >
            <LockClosedIcon class="h-6 w-6 text-red-600" />
          </div>
          <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              {{ t("logout") }}
            </h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500">
                {{ t("enter_password") }}
              </p>

              <div class="mt-4 block flex justify-center sm:justify-start">
                <div class="w-3/4 mx-auto sm:mx-0">
                  <ui-input
                    type="password"
                    :placeholder="t('password')"
                    ref="password"
                    v-model="form.password"
                    :error="form.errors.password"
                    :invalid="'password' in form.errors"
                    @keyup.enter="logoutOtherBrowserSessions"
                  >
                  </ui-input>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <ui-button
            backgroundStyling="bg-red-600 hover:bg-red-700"
            focusColorStyling="focus:ring-red-500"
            type="button"
            :disabled="form.processing"
            @click="logoutOtherBrowserSessions"
            class="sm:ml-3 w-full sm:w-auto flex justify-center"
            >{{ t("logout") }}</ui-button
          >
          <ui-button
            backgroundStyling="bg-white"
            borderStyling="border border-gray-300"
            textColorStyling="text-gray-700 hover:text-gray-500"
            type="button"
            @click="closeModal"
            class="mt-3 sm:mt-0 w-full sm:w-auto flex justify-center"
            >{{ t("cancel") }}</ui-button
          >
        </div>
      </ui-modal>
    </template>
  </jet-action-section>
</template>

<script>
import {
  DeviceMobileIcon,
  DesktopComputerIcon,
  LockClosedIcon,
} from "@heroicons/vue/outline";
import JetActionSection from "@/Jetstream/ActionSection";
import { useI18n } from "vue-i18n";

export default {
  emits: ["showMessage"],

  props: ["sessions"],

  setup() {
    const { t } = useI18n();
    return { t };
  },

  components: {
    DeviceMobileIcon,
    DesktopComputerIcon,
    LockClosedIcon,
    JetActionSection,
  },

  data() {
    return {
      confirmingLogout: false,

      form: this.$inertia.form({
        password: "",
      }),
    };
  },

  methods: {
    confirmLogout() {
      this.confirmingLogout = true;

      setTimeout(() => this.$refs.password.$refs.input.focus(), 250);
    },

    logoutOtherBrowserSessions() {
      this.form.delete(route("other-browser-sessions.destroy"), {
        preserveScroll: true,
        onSuccess: () => {
          this.$emit("showMessage", "success", this.t("success"));
          this.closeModal();
        },
        onError: () => this.$refs.password.$refs.input.focus(),
        onFinish: () => this.form.reset(),
      });
    },

    closeModal() {
      this.confirmingLogout = false;

      this.form.reset();
    },
  },
};
</script>

<i18n>
{
  "es": {
    "logout_other_sessions": "Sesiones de navegadores",
    "logout_other_sessions_description": "Administra y cierra tus sesiones activas en otros navegadores y dispositivos.",
    "explanation": "Si es necesario, puedes cerrar todas tus otras sesiones en otros navegadores a través de todos tus dispositivos. Si crees que tu cuenta ha sido comprometida, debes también cambiar tu contraseña.",
    "this_device": "Este dispositivo",
    "last_active": "Última actividad",
    "logout": "Cerrar sesiones en otros navegadores",
    "enter_password": "Porfavor ingresa tu contraseña para confirmar que deseas cerrar tus sesiones de los navegadores de todos tus demás dispositivos.",
    "password": "Contraseña",
    "success": "Sessiones cerradas correctamente",
    "cancel": "Cancelar"
  }
}
</i18n>